import { Link } from "react-router-dom";
import { GraduationCap, Users, Building2, HeartHandshake, Map, Bus, BedDouble, Sparkles, UtensilsCrossed, ShieldCheck, ArrowRight } from "lucide-react";
import { IMAGES } from "@/data/siteData";

const GROUPS = [
    { title: "Schools", track: "Track A", desc: "Educational tours, school picnics, adventure camps and experiential learning.", cta: "Plan a School Trip", to: "/school-trips", img: IMAGES.students, icon: GraduationCap },
    { title: "Colleges", track: "Track B", desc: "Industrial visits, college tours, adventure trips, cultural experiences and student excursions.", cta: "Plan a College Trip", to: "/college-trips", img: IMAGES.collegeFriends, icon: Users },
    { title: "Corporates", track: "Track C", desc: "Offsites, team outings, MICE trips, conferences, incentive travel and employee experiences.", cta: "Plan a Corporate Trip", to: "/corporate-trips", img: IMAGES.corporate, icon: Building2 },
    { title: "Groups", track: "Track D", desc: "Friends, communities, clubs, families and special-interest groups.", cta: "Build My Group Trip", to: "/group-tours", img: IMAGES.groupTravel, icon: HeartHandshake },
];

export function GroupTypes() {
    return (
        <section id="groups" className="py-20 border-b border-stone-300/70" data-testid="group-types-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <p className="font-script text-3xl sm:text-4xl text-saffron -rotate-1 inline-block">pick your tribe</p>
                <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl text-navy tracking-tight">Who Are You GhumneChaley-ing With?</h2>
                <p className="mt-2 text-sm text-stone-500 uppercase tracking-wider font-bold">Four group dynamics. One execution machine.</p>

                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {GROUPS.map((g) => (
                        <div key={g.title} className="group bg-white rounded-2xl border border-stone-300 overflow-hidden flex flex-col hover:-translate-y-1 transition-transform" data-testid={`group-card-${g.title.toLowerCase()}`}>
                            <div className="relative h-40 overflow-hidden">
                                <img src={g.img} alt={`${g.title} group trip`} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                <span className="absolute top-3 left-3 bg-navy text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full">{g.track}</span>
                            </div>
                            <div className="p-6 flex flex-col flex-1">
                                <div className="flex items-center gap-2">
                                    <g.icon className="h-5 w-5 text-saffron" />
                                    <h3 className="font-display font-black text-xl text-navy">{g.title}</h3>
                                </div>
                                <p className="mt-2 text-xs text-stone-600 leading-relaxed flex-1">{g.desc}</p>
                                <Link to={g.to} data-testid={`group-cta-${g.title.toLowerCase()}`} className="mt-5 inline-flex items-center gap-1.5 text-xs font-display font-black uppercase tracking-wider text-saffron hover:text-saffron-dark">
                                    {g.cta} <ArrowRight className="h-3.5 w-3.5" />
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

const SERVICES = [
    { title: "Trip Planning", desc: "Complete itinerary planning based on budget, group size and objectives.", icon: Map },
    { title: "Transport", desc: "Buses, coaches, flights, transfers and local transportation.", icon: Bus },
    { title: "Stay", desc: "Hotels, resorts, hostels and experiential stays.", icon: BedDouble },
    { title: "Experiences", desc: "Adventure activities, sightseeing, workshops, cultural experiences and entertainment.", icon: Sparkles },
    { title: "Food & Hospitality", desc: "Meals, restaurants, catering and complete hospitality management.", icon: UtensilsCrossed },
    { title: "On-Ground Execution", desc: "Trip coordinators, tour managers, permissions, logistics, emergency support and execution.", icon: ShieldCheck },
];

export function Services() {
    return (
        <section id="services" className="py-20 bg-white border-b border-stone-300/70" data-testid="services-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <p className="font-script text-3xl sm:text-4xl text-saffron -rotate-1 inline-block">zero jhanjhat</p>
                <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl text-navy tracking-tight">You Pick the Experience. We Handle Everything Else.</h2>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {SERVICES.map((s, i) => (
                        <div key={s.title} className="border border-stone-300 p-6 rounded-2xl bg-cream hover:border-saffron transition-colors" data-testid={`service-card-${i}`}>
                            <div className="flex items-center justify-between">
                                <s.icon className="h-6 w-6 text-saffron" />
                                <span className="font-display font-black text-2xl text-stone-200">0{i + 1}</span>
                            </div>
                            <h3 className="mt-4 font-display font-black text-lg text-navy">{s.title}</h3>
                            <p className="mt-2 text-sm text-stone-600 leading-relaxed">{s.desc}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-12 rounded-2xl bg-navy px-8 py-10 text-center" data-testid="services-bold-statement">
                    <p className="font-display font-black text-2xl sm:text-3xl text-white tracking-tight">
                        From the first call to the final goodbye — <span className="text-gold">we've got the trip covered.</span>
                    </p>
                </div>
            </div>
        </section>
    );
}
